import { useState } from 'react';
import { Button, cn, Flex, Icon } from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';

export type TRainCompactionStatus = 'running' | 'complete' | 'failed';

export interface IRainCompactionCardProps {
  status: TRainCompactionStatus;
  content?: string;
  errorMessage?: string;
  className?: string;
}

const statusIcon = (
  status: TRainCompactionStatus,
): { type: 'Loader2' | 'Sparkles' | 'CircleAlert'; className: string } => {
  if (status === 'complete')
    return { type: 'Sparkles', className: 'text-muted-foreground' };
  if (status === 'failed')
    return { type: 'CircleAlert', className: 'text-destructive' };
  return { type: 'Loader2', className: 'text-muted-foreground animate-spin' };
};

export function RainCompactionCard({
  status,
  content,
  errorMessage,
  className,
}: IRainCompactionCardProps) {
  const { t } = useTranslation('rain');
  const [open, setOpen] = useState(false);

  const icon = statusIcon(status);
  const title =
    status === 'complete'
      ? t('rain.compaction.complete')
      : status === 'failed'
        ? t('rain.compaction.failed')
        : t('rain.compaction.running');
  const body = status === 'failed' ? errorMessage : content;

  return (
    <Flex
      direction="column"
      gap={2}
      className={cn(
        'border border-border bg-muted/40 rounded-md p-3 w-full',
        className,
      )}
      data-testid="rain-compaction-card"
      data-status={status}
    >
      <Flex direction="row" align="center" gap={2}>
        <Icon
          type={icon.type}
          size="sm"
          className={cn('shrink-0', icon.className)}
        />
        <span className="text-sm font-medium text-foreground">{title}</span>
      </Flex>

      {body && (
        <Flex direction="column" gap={1}>
          <Button
            variant="ghost"
            size="sm"
            className="self-start text-xs text-muted-foreground"
            onClick={() => setOpen(prev => !prev)}
            data-testid="rain-compaction-toggle"
          >
            <Icon type={open ? 'ChevronUp' : 'ChevronDown'} size="sm" />
            {open ? t('rain.compaction.collapse') : t('rain.compaction.expand')}
          </Button>
          {open && (
            <pre
              className={cn(
                'text-xs bg-card border border-border rounded-md p-2 overflow-x-auto whitespace-pre-wrap break-words',
                status === 'failed' ? 'text-destructive' : 'text-foreground',
              )}
              data-testid="rain-compaction-body"
            >
              {body}
            </pre>
          )}
        </Flex>
      )}
    </Flex>
  );
}
